import axios, { AxiosHeaders } from 'axios';
import { jwtDecode } from 'jwt-decode';

import { APP_CFG } from '@/configs';
import { getCurrentAuthToken, requestAuthTokenRefresh, setCurrentAuthToken } from '@/hooks/auth/utils/auth-token-registry';
import { resolveWalletSliceKey } from '@/hooks/auth/utils/wallet.util';
import type { AuthType } from '@/hooks/auth/types/auth-type.type';
import type { WalletSliceKey } from '@/hooks/auth/types/wallet-slice.type';

export const apiClient = axios.create({
	baseURL: APP_CFG.API_URL,
});

apiClient.interceptors.request.use((config) => {
	const currentToken = getCurrentAuthToken();
	if (!currentToken) {
		return config;
	}

	const headers = AxiosHeaders.from(config.headers);
	if (!headers.has('Authorization')) {
		headers.set('Authorization', `Bearer ${currentToken}`);
	}
	config.headers = headers;

	return config;
});

apiClient.interceptors.response.use(
	(response) => {
		const renewedTokenHeader = response.headers?.['x-renewed-jwt'] ?? response.headers?.['X-Renewed-JWT'];
		if (renewedTokenHeader) {
			const token = Array.isArray(renewedTokenHeader) ? renewedTokenHeader[0] : renewedTokenHeader;
			if (typeof token === 'string' && token.length > 0) {
				const walletType = resolveWalletTypeFromToken(token);
				setCurrentAuthToken(token, { walletType });
			}
		}
		return response;
	},
	(error) => {
		if (axios.isAxiosError(error)) {
			const status = error.response?.status;
			if (status === 401) {
				const message = extractErrorMessage(error.response?.data);
				if (message?.toLowerCase().includes(SIGNATURE_INVALID_MESSAGE_FRAGMENT)) {
					requestAuthTokenRefresh();
				}
			}
		}
		return Promise.reject(error);
	},
);

const SIGNATURE_INVALID_MESSAGE_FRAGMENT = 'authentication signature';

type AuthTokenPayload = { authType?: AuthType };

function resolveWalletTypeFromToken(token: string): WalletSliceKey | null {
	try {
		const payload = jwtDecode<AuthTokenPayload>(token);
		const walletType = resolveWalletSliceKey(payload?.authType);
		if (walletType) {
			return walletType;
		}
	} catch (error) {
		console.warn('Failed to decode renewed auth token', error);
	}
	return null;
}

function extractErrorMessage(data: unknown): string | undefined {
	if (!data) {
		return undefined;
	}

	if (typeof data === 'string') {
		return data;
	}

	if (typeof data === 'object') {
		const payload = data as { message?: unknown; error?: unknown };
		const { message, error } = payload;

		if (typeof message === 'string') {
			return message;
		}

		if (Array.isArray(message)) {
			const firstText = message.find((item): item is string => typeof item === 'string');
			if (firstText) {
				return firstText;
			}
		}

		if (typeof error === 'string') {
			return error;
		}
	}

	return undefined;
}
